// submit-feedback.ts — 19BM Work-area F · subjective-feedback reporting helper.
//
// 陛下最初的问题("组件行为有问题但报不上来") 已被 A 阶段 (behavior-error) 解决了一半:
// 代码捕获的非致命错现在能上报。但还有另一半——"我觉得不对但不是错误"的主观体验
// 反馈——没有任何通道。错误上报覆盖不了这种情形: 不报错、但就是感觉哪里不对。
//
// 本模块是 subjective-feedback 的单一入口。F 阶段的 FeedbackPanel 通过快捷键 (Cmd+/
// 或 Ctrl+/) 弹出，用户可选类别 + 选填备注，提交后走 recordEvent → D1 client_events →
// admin feed。与 behavior-error 的关键区别:
//   - 不是错误 → 不进 ERROR_EVENT_TYPES → recordEvent 不 stamp errorId → 不污染
//     error-centric feed / agent 水位线
//   - 用户主动表达 (vs 代码捕获) → payload 带 category + note
//   - 去重粒度是 viewPath (vs behavior-error 的 viewPath|userAction|code) —— 同一页面
//     30s 内只允许提交一次，防误触连击

import { recordEvent } from '../storage/idb.js';

/** 30s dedup window — same value as behavior-error (hub §3.3 convention). */
const DEDUP_WINDOW_MS = 30_000;

/**
 * In-memory dedup set. Key = viewPath. Entries auto-expire after DEDUP_WINDOW_MS.
 * Process-memory only — page refresh clears it (a refresh is a fresh session;
 * re-submitting after refresh is signal, not noise).
 */
const recentSubmissions = new Set<string>();

/** Feedback categories surfaced in the panel. The `code` stored in the event payload. */
export type FeedbackCategory = 'off' | 'missing' | 'slow' | 'other';

export interface SubmitFeedbackInput {
  /** URL pathname where the user opened the feedback panel (location.pathname). */
  viewPath: string;
  /** One of the 4 categories. The primary signal for the operator. */
  category: FeedbackCategory;
  /** Optional free-text note (≤500 chars, enforced by the textarea maxLength). */
  note?: string;
}

/**
 * Outcome of a submit attempt. Session 3 (19BM audit): changed from boolean
 * because the old `catch { return true }` made IDB failures look like success
 * (HIGH-2 audit bug — user saw "已记录" toast but feedback was silently lost,
 * violating the observability-closed-loop principle 19BM was built for).
 *
 *   - 'ok'        — recorded successfully
 *   - 'duplicate' — blocked by 30s dedup (caller shows "稍后再试")
 *   - 'failed'    — IDB write threw; caller shows "记录失败，请重试" + console.warn
 */
export type SubmitFeedbackResult = 'ok' | 'duplicate' | 'failed';

/**
 * Submit a subjective-feedback event. Dedup'd per viewPath within a 30s window.
 * Never throws — telemetry must not break the UI. Caller branches on result
 * to show the correct toast (ok / duplicate / failed).
 */
export async function submitFeedback(input: SubmitFeedbackInput): Promise<SubmitFeedbackResult> {
  const { viewPath, category, note } = input;
  if (recentSubmissions.has(viewPath)) return 'duplicate';

  recentSubmissions.add(viewPath);
  // Clear after the window so the user can submit again later on the same page.
  setTimeout(() => {
    recentSubmissions.delete(viewPath);
  }, DEDUP_WINDOW_MS);

  try {
    await recordEvent({
      type: 'subjective-feedback',
      summary: `反馈: ${category}${note ? ` — ${note.slice(0, 120)}` : ''}`,
      viewPath,
      payload: {
        category,
        note: note?.slice(0, 500) ?? null,
      },
    });
    return 'ok';
  } catch (err) {
    // Telemetry must never break the calling code path — but it must not lie
    // about success either (19BM observability closed-loop). Surface 'failed'
    // so the UI shows a retry prompt; log in DEV for operator visibility.
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console -- telemetry failure signal — must be observable in production
      console.warn('[feedback] recordEvent failed — not recorded:', err);
    }
    return 'failed';
  }
}

/**
 * Test-only: clear the dedup set. Exported so unit tests can reset between
 * cases without waiting 30s. NOT for production use.
 */
export function __resetFeedbackDedupForTests(): void {
  recentSubmissions.clear();
}
